import { useLocation, matchPath, useRoutes } from "react-router-dom";
import { useEffect } from "react";
import type { IndexRouteObject, RouteObject } from "react-router-dom";

import { getValue, isNil } from "../utils";
import { RouteBox } from "../styles/common/style";
import {
  Module,
  NonIndexRouteWithFullPath,
  SubModule,
} from "../models/Modules";
import { UserModuleResponse } from "../models/Api/Modules";
import AuthGuard from "../modules/auth/AuthGuard";
import { RouteUrlInfo } from "../models/User";
import ErrorPage from "../components/errorPages/404";
import routeMapping, { Layout } from "./routePath";
import {
  prepareMenuItems,
  setActiveModuleSubModule,
  updateRouteObjectFullRoutePath,
} from "../utils/authentication/authorization";

export interface RouteProps {
  moduleData: UserModuleResponse;
  saveRouteUrlInfo: (routeUrlInfo: RouteUrlInfo) => void;
}

const RoutesList: React.FC<RouteProps> = ({ moduleData, saveRouteUrlInfo }) => {
  const userModule = moduleData.response;
  const { pathname } = useLocation();

  useEffect(() => {
    setActiveModuleSubModule(userModule, pathname, saveRouteUrlInfo, matchPath);
  }, [userModule, pathname]);

  const prepareAuthRoute = (
    userModuleResponse: Module[],
    routeMapping: NonIndexRouteWithFullPath[]
  ): RouteObject[] => {
    const routeMapObject: RouteObject[] = [];
    const menuDataObject = prepareMenuItems(userModuleResponse);
    const routeMappingObjectfullPath =
      updateRouteObjectFullRoutePath(routeMapping);

    const createAuthGuardComponent = (
      isAuthenticated: boolean,
      element: React.ReactNode
    ) => (
      <AuthGuard authenticated={isAuthenticated} userModuleResponse={userModuleResponse}>
        {element}
      </AuthGuard>
    );

    const processRouteObject = (
      mapModuleItemCopy: any,
      menuDataObjectIndex: number
    ) => {
      const isAuthenticated = menuDataObjectIndex > -1;
      mapModuleItemCopy.element = createAuthGuardComponent(
        isAuthenticated,
        mapModuleItemCopy.element
      );
      mapModuleItemCopy.fullRoutePath = isAuthenticated
        ? menuDataObject[menuDataObjectIndex].routeUrl
        : mapModuleItemCopy.fullRoutePath;
      mapModuleItemCopy.children = [];
    };

    const updateChildren = (
      parentChildren: IndexRouteObject[],
      childRoute: RouteObject | any,
      menuModule: Module | SubModule
    ): void => {
      const childCopy = { ...childRoute };
      const subModules = menuModule?.subModules || [];
      const subModuleIndex = subModules.findIndex(
        (x: Module | SubModule) => x.routeUrl === childCopy.path
      );

      const isAuthenticated = subModuleIndex > -1;
      childCopy.element = createAuthGuardComponent(
        isAuthenticated,
        childCopy.element
      );
      childCopy.fullRoutePath = isAuthenticated
        ? subModules[subModuleIndex]?.fullRoutePath
        : childCopy.fullRoutePath;

      if (childRoute.children) {
        childCopy.children = [];
        childRoute.children.forEach((nestedChild: RouteObject) =>
          updateChildren(
            childCopy.children,
            nestedChild,
            subModules[subModuleIndex] || {}
          )
        );
      }

      parentChildren.push(childCopy);
    };

    routeMappingObjectfullPath.forEach((mapModuleItem: NonIndexRouteWithFullPath) => {
      const mapModuleItemCopy: any = { ...mapModuleItem };
      const menuDataObjectIndex = menuDataObject.findIndex(
        (x) => x.routeUrl === mapModuleItemCopy.path
      );

      processRouteObject(mapModuleItemCopy, menuDataObjectIndex);

      mapModuleItem.children?.forEach((childRoute: RouteObject) =>
        updateChildren(
          mapModuleItemCopy.children,
          childRoute,
          menuDataObject[menuDataObjectIndex]
        )
      );

      routeMapObject.push(mapModuleItemCopy);
    });

    return convertToSingleLevelRoute(routeMapObject, userModuleResponse);
  };

  const convertToSingleLevelRoute = (
    routeMapObject: any,
    userModuleResponse: any
  ) => {
    const singleLevelroutes: RouteObject[] | any = [
      {
        path: "/",
        element: <Layout />,
        children: [
          { index: true, element: null },
          {
            path: "*",
            element:
              getValue(userModuleResponse, "[0].moduleId", 0) !== -1 ? (
                <ErrorPage />
              ) : null,
          },
        ],
      },
    ];

    if (
      routeMapObject &&
      routeMapObject.length !== 0 &&
      !isNil(userModuleResponse)
    ) {
      const rootUrlElementObject = routeMapObject.find(
        (routeObject: any) =>
          routeObject.path ===
          getValue(userModuleResponse, "[0].routeUrl", null)
      );
      singleLevelroutes[0].children[0].element = getValue(
        rootUrlElementObject,
        "element",
        null
      );
    }

    const pushRouteElement = (
      pageRoutesNested: RouteObject[],
      routeObj: RouteObject | any
    ) => {
      const { fullRoutePath, element, children } = routeObj;
      pageRoutesNested.push({
        path: fullRoutePath,
        element: element,
      });
      if (children && children.length > 0) {
        for (const childRouteObj of children) {
          pushRouteElement(pageRoutesNested, childRouteObj);
        }
      }
    };
    for (const routeObj of routeMapObject) {
      pushRouteElement(singleLevelroutes[0].children, routeObj);
    }
    return singleLevelroutes;
  };

  const dynamicRouteData = prepareAuthRoute(userModule, routeMapping);
  const routeElements = useRoutes(dynamicRouteData);

  return (
    <>
      <RouteBox>{routeElements}</RouteBox>
    </>
  );
};

export default RoutesList;
